import React, { useState } from "react";
import { Trash2, Ticket, X } from "lucide-react";
import { TagPreview } from "../../lib/tagShapes";

export const calcCartDiscount = (cart, tiers) => {
  const single = cart.filter((i) => !i.bundle_id);
  const qty = single.reduce((s, i) => s + i.quantity, 0);
  const eligible = single.reduce((s, i) => s + i.quantity * (i.unit_price || 0), 0);
  let best = null;
  [...(tiers || [])]
    .sort((a, b) => Number(a.min_qty || 0) - Number(b.min_qty || 0))
    .forEach((t) => { if (qty >= Number(t.min_qty || 0) && Number(t.percent || 0) > 0) best = t; });
  const nextTier = [...(tiers || [])]
    .sort((a, b) => Number(a.min_qty || 0) - Number(b.min_qty || 0))
    .find((t) => Number(t.min_qty || 0) > qty && Number(t.percent || 0) > 0);
  return {
    amount: best && eligible > 0 ? +((eligible * Number(best.percent)) / 100).toFixed(2) : 0,
    percent: best ? Number(best.percent) : 0,
    qty,
    nextTier,
  };
};

export const OrderCart = ({ cart, settings, shipMethodId, onSelectShipping, onRemove, voucher, onApplyVoucher, onRemoveVoucher }) => {
  const [voucherInput, setVoucherInput] = useState("");
  const basePrice = Number(settings?.base_price || 0);
  const freeOver = Number(settings?.shipping_free_over || 0);
  const shipMethods = (settings?.shipping_methods || []).filter((m) => m.active);
  const shipMethod = shipMethods.find((m) => m.id === shipMethodId) || shipMethods[0];
  const subtotal = cart.reduce((s, i) => s + i.quantity * (i.unit_price ?? basePrice), 0);
  const discount = calcCartDiscount(cart, settings?.quantity_discounts);
  const shipRate = shipMethod ? Number(shipMethod.price || 0) : Number(settings?.shipping_flat || 0);
  let shipping = cart.length && !(freeOver && subtotal >= freeOver) ? shipRate : 0;

  const belowMinSpend = voucher && Number(voucher.min_spend || 0) > 0 && subtotal < Number(voucher.min_spend);
  let voucherAmount = 0;
  if (voucher && !belowMinSpend && cart.length) {
    if (voucher.type === "free_shipping") shipping = 0;
    else if (voucher.type === "percent") voucherAmount = +(((subtotal - discount.amount) * Number(voucher.value)) / 100).toFixed(2);
    else if (voucher.type === "free_tags") {
      const units = [];
      cart.filter((i) => !i.bundle_id).forEach((i) => { for (let k = 0; k < i.quantity; k++) units.push(Number(i.unit_price || 0)); });
      units.sort((a, b) => a - b);
      voucherAmount = Math.min(+units.slice(0, Number(voucher.value)).reduce((s, u) => s + u, 0).toFixed(2), subtotal - discount.amount);
    }
    else voucherAmount = +Math.min(Number(voucher.balance), Math.max(0, subtotal - discount.amount + shipping)).toFixed(2);
  }
  const total = Math.max(0, subtotal - discount.amount - voucherAmount + shipping);
  const gstRate = Number(settings?.gst_rate || 0);
  const gst = gstRate ? (total * gstRate) / (100 + gstRate) : 0;

  return (
    <div className="bg-warmwhite rounded-[2rem] border border-plum-deep/10 p-6" data-testid="order-cart">
      <h4 className="font-serif-display uppercase text-plum-deep text-lg tracking-[0.1em]">YOUR ORDER ({cart.length})</h4>
      {cart.length === 0 ? (
        <p className="mt-3 text-sm text-plum-deep/60">No tags added yet — build one and add it to your order.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {cart.map((item, i) => (
            <li key={item.uid || i} className="flex items-center gap-3 bg-cream rounded-2xl p-3" data-testid={`cart-item-${i}`}>
              <TagPreview shapeKey={item.shape_key} hex={item.colour_hex} line1={item.text_line1} line2={item.text_line2} size={item.size} icon={item.icon} className="w-10 h-14 shrink-0" />
              <div className="flex-1 min-w-0">
                {item.bundle_id ? (
                  <>
                    <p className="text-sm font-medium truncate">{item.bundle_name}</p>
                    <p className="text-xs text-plum-deep/60">{item.texts.length} tags · {item.shape_name} · {item.colour_name}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium truncate">"{item.text_line1}"{item.text_line2 ? ` / ${item.text_line2}` : ""}</p>
                    <p className="text-xs text-plum-deep/60">{item.shape_name}{item.size ? ` ${item.size}` : ""} · {item.colour_name} · ×{item.quantity}</p>
                  </>
                )}
              </div>
              <span className="text-sm font-semibold">${(item.quantity * (item.unit_price ?? basePrice)).toFixed(2)}</span>
              <button onClick={() => onRemove(i)} data-testid={`cart-remove-${i}`} className="text-plum-deep/40 hover:text-magenta"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {cart.length > 0 && (
        <div className="mt-4 space-y-1 font-serif-display">
          <div className="mb-3" data-testid="voucher-box">
            {voucher ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.14em] font-bold" data-testid="voucher-applied">
                  <Ticket className="w-3.5 h-3.5" /> {voucher.code}
                  <button onClick={onRemoveVoucher} data-testid="voucher-remove-btn" aria-label="Remove voucher" className="hover:text-lemon"><X className="w-3.5 h-3.5" /></button>
                </span>
                {voucher.type === "free_shipping" && <span className="text-[11px] text-forest">FREE SHIPPING VOUCHER</span>}
                {voucher.type === "fixed" && <span className="text-[11px] text-forest">${Number(voucher.balance).toFixed(2)} CREDIT AVAILABLE</span>}
                {belowMinSpend && <span className="text-[11px] text-magenta" data-testid="voucher-min-warning">SPEND ${Number(voucher.min_spend).toFixed(2)}+ TO USE THIS VOUCHER</span>}
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={voucherInput}
                  onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                  placeholder="GOT A VOUCHER?"
                  data-testid="voucher-input"
                  className="flex-1 rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-xs tracking-[0.12em] focus:outline-none focus:border-forest font-sans"
                />
                <button
                  onClick={() => voucherInput.trim() && onApplyVoucher(voucherInput.trim())}
                  data-testid="voucher-apply-btn"
                  className="rounded-full bg-plum text-cream px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-bold hover:bg-forest transition-colors"
                >
                  APPLY
                </button>
              </div>
            )}
          </div>
          {shipMethods.length > 1 && (
            <div className="mb-3 space-y-1.5" data-testid="shipping-method-picker">
              <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold">SHIPPING OPTION</p>
              {shipMethods.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`shipping-method-${m.id}`}>
                  <input type="radio" name="ship" checked={shipMethod?.id === m.id} onChange={() => onSelectShipping(m.id)} className="accent-magenta" />
                  <span className="uppercase tracking-[0.06em] text-plum-deep/80">{m.name}</span>
                  <span className="ml-auto">{Number(m.price) === 0 ? "FREE" : `$${Number(m.price).toFixed(2)}`}</span>
                </label>
              ))}
            </div>
          )}
          <p className="flex justify-between text-sm text-plum-deep/70">
            <span className="uppercase tracking-[0.1em]">SUBTOTAL</span>
            <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </p>
          {discount.amount > 0 && (
            <p className="flex justify-between text-sm text-forest">
              <span className="uppercase tracking-[0.1em]">QUANTITY DISCOUNT ({discount.percent}%)</span>
              <span data-testid="cart-discount">−${discount.amount.toFixed(2)}</span>
            </p>
          )}
          {voucherAmount > 0 && (
            <p className="flex justify-between text-sm text-forest">
              <span className="uppercase tracking-[0.1em]">VOUCHER {voucher.code}</span>
              <span data-testid="cart-voucher">−${voucherAmount.toFixed(2)}</span>
            </p>
          )}
          <p className="flex justify-between text-sm text-plum-deep/70">
            <span className="uppercase tracking-[0.1em]">SHIPPING</span>
            <span data-testid="cart-shipping">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
          </p>
          {discount.nextTier && (
            <p className="text-[11px] text-forest" data-testid="discount-hint">
              Add {Number(discount.nextTier.min_qty) - discount.qty} more tag{Number(discount.nextTier.min_qty) - discount.qty === 1 ? "" : "s"} for {Number(discount.nextTier.percent)}% off
            </p>
          )}
          {freeOver > 0 && shipping > 0 && (
            <p className="text-[11px] text-forest">Free shipping on orders over ${freeOver.toFixed(0)}</p>
          )}
          <p className="flex justify-between text-lg pt-1 border-t border-plum-deep/10">
            <span className="uppercase tracking-[0.1em]">TOTAL</span>
            <span data-testid="cart-total">${total.toFixed(2)} NZD</span>
          </p>
          {gst > 0 && (
            <p className="text-[11px] text-plum-deep/50 text-right" data-testid="cart-gst">INCLUDES GST ({gstRate}%): ${gst.toFixed(2)}</p>
          )}
        </div>
      )}
    </div>
  );
};
